import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PackagePlus, Pencil, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea, Toggle } from '@/components/ui/Input';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { EmptyState, PageLoader } from '@/components/ui/States';
import { useToast } from '@/components/ui/Toast';
import { formatBytes, formatIdr, formatNumber } from '@/lib/format';

/**
 * Pengelolaan katalog add-on.
 *
 * Add-on adalah kuota tambahan di atas paket, dan hanya empat jenis yang
 * dijual, semuanya berupa angka yang bisa dijumlahkan. Add-on yang menyalakan
 * fitur sengaja tidak ada: fitur yang bisa dibeli terpisah membuat kombinasi
 * paket meledak sampai tidak ada lagi yang bisa menjawab "paket ini
 * sebenarnya bisa apa".
 */

const JENIS = [
  ['records', 'Baris data'],
  ['storage', 'Penyimpanan'],
  ['jobs', 'Jumlah job'],
  ['schedules', 'Jumlah jadwal'],
];

const PAKET = ['free', 'starter', 'growth', 'pro', 'business'];

const NAMA_JENIS = Object.fromEntries(JENIS);

/** Penyimpanan disimpan dalam byte, diisi orang dalam MB. */
const keMb = (byte) => Math.round((byte / (1024 * 1024)) * 10) / 10;

const sebutBesaran = (type, amount) => {
  if (type === 'storage') return formatBytes(amount);
  if (type === 'records') return `${formatNumber(amount)} baris`;
  if (type === 'jobs') return `${formatNumber(amount)} job`;
  return `${formatNumber(amount)} jadwal`;
};

const KOSONG = {
  name: '',
  description: '',
  type: 'records',
  besaran: '',
  priceIdr: '',
  maxQuantity: '20',
  availableForPlans: [],
  active: true,
  sortOrder: '0',
};

const DialogAddOn = ({ awal, onSimpan, menyimpan, onClose }) => {
  const [f, setF] = useState(() =>
    awal
      ? {
          name: awal.name,
          description: awal.description ?? '',
          type: awal.type,
          besaran: String(awal.type === 'storage' ? keMb(awal.amount) : awal.amount),
          priceIdr: String(awal.priceIdr),
          maxQuantity: String(awal.maxQuantity),
          availableForPlans: awal.availableForPlans ?? [],
          active: awal.active,
          sortOrder: String(awal.sortOrder ?? 0),
        }
      : KOSONG,
  );

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const togglePaket = (id) =>
    set(
      'availableForPlans',
      f.availableForPlans.includes(id)
        ? f.availableForPlans.filter((x) => x !== id)
        : [...f.availableForPlans, id],
    );

  const kirim = () =>
    onSimpan({
      name: f.name.trim(),
      description: f.description.trim(),
      type: f.type,
      // Besaran storage diisi dalam MB lalu diubah ke byte di sini. Server
      // menyimpannya dalam byte supaya sama dengan cara kuota dihitung.
      amount: f.type === 'storage' ? Math.round(Number(f.besaran) * 1024 * 1024) : Number(f.besaran),
      priceIdr: Number(f.priceIdr),
      maxQuantity: Number(f.maxQuantity),
      availableForPlans: f.availableForPlans,
      active: f.active,
      sortOrder: Number(f.sortOrder),
    });

  const sah = f.name.trim() && Number(f.besaran) > 0 && f.priceIdr !== '';

  return (
    <Modal
      open
      onClose={onClose}
      title={awal ? `Ubah ${awal.name}` : 'Add-on baru'}
      size="lg"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Batal
          </Button>
          <Button loading={menyimpan} disabled={!sah} onClick={kirim}>
            Simpan
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Nama" required hint="Yang dilihat pembeli, mis. Tambah 5.000 Baris.">
          {(props) => (
            <Input {...props} value={f.name} onChange={(e) => set('name', e.target.value)} />
          )}
        </Field>

        <Field label="Keterangan" hint="Opsional. Satu kalimat kenapa orang membutuhkannya.">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={f.description}
              onChange={(e) => set('description', e.target.value)}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Jenis kuota" required>
            {(props) => (
              <Select {...props} value={f.type} onChange={(e) => set('type', e.target.value)}>
                {JENIS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Besaran per satuan"
            required
            hint={f.type === 'storage' ? 'Dalam MB.' : 'Jumlah yang ditambahkan.'}
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min="1"
                value={f.besaran}
                onChange={(e) => set('besaran', e.target.value)}
              />
            )}
          </Field>

          <Field label="Harga per satuan" required hint="Untuk satu periode penuh.">
            {(props) => (
              <Input
                {...props}
                type="number"
                min="0"
                value={f.priceIdr}
                onChange={(e) => set('priceIdr', e.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Maksimal sekali beli" hint="Menahan salah ketik saat memilih jumlah.">
            {(props) => (
              <Input
                {...props}
                type="number"
                min="1"
                value={f.maxQuantity}
                onChange={(e) => set('maxQuantity', e.target.value)}
              />
            )}
          </Field>
          <Field label="Urutan tampil" hint="Angka kecil tampil lebih dulu.">
            {(props) => (
              <Input
                {...props}
                type="number"
                min="0"
                value={f.sortOrder}
                onChange={(e) => set('sortOrder', e.target.value)}
              />
            )}
          </Field>
        </div>

        <Field
          label="Hanya untuk paket tertentu"
          hint="Kosongkan berarti berlaku untuk semua paket yang boleh membeli add-on."
        >
          {() => (
            <div className="flex flex-wrap gap-2">
              {PAKET.map((id) => {
                const aktif = f.availableForPlans.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={aktif}
                    onClick={() => togglePaket(id)}
                    className={
                      aktif
                        ? 'rounded-lg border border-accent-600 bg-accent-50 px-3 py-1.5 text-sm font-medium capitalize dark:bg-accent-500/10'
                        : 'rounded-lg border border-[var(--border-subtle)] px-3 py-1.5 text-sm capitalize text-[var(--text-muted)]'
                    }
                  >
                    {id}
                  </button>
                );
              })}
            </div>
          )}
        </Field>

        <Toggle
          checked={f.active}
          onChange={(v) => set('active', v)}
          label="Dijual"
          description="Kalau dimatikan, add-on tidak muncul di halaman pembelian. Yang sudah dibeli tetap berlaku."
        />
      </div>
    </Modal>
  );
};

export const AddOnsAdminPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [menyunting, setMenyunting] = useState(null);
  const [hapusTarget, setHapusTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['platform-addons'],
    queryFn: () => api.get('/platform/addons'),
  });

  const segarkan = () => {
    queryClient.invalidateQueries({ queryKey: ['platform-addons'] });
    queryClient.invalidateQueries({ queryKey: ['addon-catalog'] });
  };

  const simpan = useMutation({
    mutationFn: (body) =>
      menyunting?.id
        ? api.patch(`/platform/addons/${menyunting.id}`, body)
        : api.post('/platform/addons', body),
    onSuccess: () => {
      toast.success(menyunting?.id ? 'Add-on diperbarui' : 'Add-on dibuat');
      setMenyunting(null);
      segarkan();
    },
    onError: (err) =>
      toast.error('Gagal menyimpan', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const hapus = useMutation({
    mutationFn: () => api.delete(`/platform/addons/${hapusTarget.id}`),
    onSuccess: (res) => {
      toast.success('Add-on dihapus', { description: res?.data?.message });
      setHapusTarget(null);
      segarkan();
    },
    onError: (err) => {
      toast.error('Gagal menghapus', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      });
      setHapusTarget(null);
    },
  });

  if (isLoading) return <PageLoader label="Memuat add-on..." />;

  const items = data?.data ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Add-on</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            Kuota tambahan yang bisa dibeli di atas paket. Harganya dihitung proporsional terhadap
            sisa periode, dan add-on berakhir bersama masa aktif paket pembelinya.
          </p>
        </div>
        <Button icon={PackagePlus} onClick={() => setMenyunting({})}>
          Add-on Baru
        </Button>
      </div>

      <Card>
        {items.length === 0 ? (
          <EmptyState
            icon={PackagePlus}
            title="Belum ada add-on"
            description="Buat satu supaya pelanggan bisa menambah kuota tanpa mengganti paket."
          />
        ) : (
          <CardBody className="space-y-6">
            {JENIS.map(([jenis, labelJenis]) => {
              const anggota = items.filter((a) => a.type === jenis);
              if (anggota.length === 0) return null;

              return (
                <section key={jenis}>
                  <h3 className="mb-2 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
                    {labelJenis}
                    <span className="ml-2 font-normal normal-case">{anggota.length} add-on</span>
                  </h3>
                  <div className="space-y-2">
                    {anggota.map((a) => (
              <div
                key={a.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--border-subtle)] px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
                    {a.name}
                    {!a.active && (
                      <Badge tone="neutral" size="sm">
                        Tidak dijual
                      </Badge>
                    )}
                  </p>
                  <p className="truncate text-xs text-[var(--text-muted)]">
                    {sebutBesaran(a.type, a.amount)} per satuan · maksimal {a.maxQuantity} sekali
                    beli
                    {a.availableForPlans?.length > 0 &&
                      ` · hanya ${a.availableForPlans.join(', ')}`}
                  </p>
                </div>

                <span className="tabular text-sm font-bold text-[var(--text-primary)]">
                  {formatIdr(a.priceIdr)}
                </span>

                <Button
                  variant="ghost"
                  size="sm"
                  icon={Pencil}
                  iconOnly
                  aria-label={`Ubah ${a.name}`}
                  onClick={() => setMenyunting(a)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Trash2}
                  iconOnly
                  aria-label={`Hapus ${a.name}`}
                  className="text-danger-500 hover:text-danger-600"
                  onClick={() => setHapusTarget(a)}
                />
              </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </CardBody>
        )}
      </Card>

      {menyunting && (
        <DialogAddOn
          awal={menyunting.id ? menyunting : null}
          menyimpan={simpan.isPending}
          onSimpan={(body) => simpan.mutate(body)}
          onClose={() => setMenyunting(null)}
        />
      )}

      <ConfirmDialog
        open={Boolean(hapusTarget)}
        onClose={() => setHapusTarget(null)}
        onConfirm={() => hapus.mutate()}
        loading={hapus.isPending}
        destructive
        title={`Hapus add-on "${hapusTarget?.name}"?`}
        description="Ia berhenti dijual, tetapi yang sudah dibeli pelanggan tetap berlaku sampai masa aktifnya habis."
        confirmLabel="Ya, hapus"
      />
    </div>
  );
};
